import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Button } from "@mui/material";
import truncate from "lodash/truncate";
import { useBanksSearch } from "@api/v1/hooks/useBanksSearch";
import { CreateOrUpdateAccountParams } from "@api";
import { useAccounts, useUpdateAccount } from "@api/v1/hooks/useAccounts";
import { GET_ACCOUNTS } from "@api/cacheKeys";
import { PageLayout } from "@components";
import { AccountEditActions } from "./AccountEditActions";
import { CreateAndEditAccountForm } from "./CreateAndEditAccountForm";
import { CreateAndUpdateResultMessage } from "./CreateAndUpdateResultMessage";

const SaveButton = styled(Button)`
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  max-width: 700px;
`;

interface CreateAndEditAccountProps {
  accountId: string;
  type: "account" | "depot";
}

export const CreateAndEditAccount = (props: CreateAndEditAccountProps) => {
  const { accountId, type } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const parsedAccountId = Number.parseInt(accountId, 10);

  const [accountValues, setAccountValues] =
    useState<CreateOrUpdateAccountParams>({
      name: "",
      type: "01_depot",
    });

  const { mutateAsync, error, status } = useUpdateAccount({
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [GET_ACCOUNTS],
      });
    },
  });
  const { data: accountsResponse, isFetching: isAccountsLoading } = useAccounts(
    {
      id: [parsedAccountId],
    },
    {
      onSuccess: (data) => {
        const [account] = data?.accounts ?? [];
        setAccountValues({
          name: account?.name ?? "",
          type: account?.type ?? "01_depot",
        });
      },
    }
  );

  const [account] = accountsResponse?.accounts ?? [];

  const { data: searchBankResponse } = useBanksSearch(
    {
      search: account?.bankCode as string,
    },
    {
      enabled: Boolean(account?.bankCode),
    }
  );

  const [bank] = searchBankResponse?.banks ?? [];

  const handleUpdateAccount = () => {
    mutateAsync({ ...accountValues, id: parsedAccountId });
  };

  const handleUpdateAccountValues = (
    params: Partial<CreateOrUpdateAccountParams>
  ) => {
    setAccountValues((values) => {
      return { ...values, ...params };
    });
  };
  const requestStatus =
    status === "success" || status === "error" ? status : undefined;

  const url = type === "depot" ? "/depots" : "/accounts";
  const accountName = account?.name ?? "";

  return (
    <PageLayout
      isFetching={isAccountsLoading}
      title={t("createAndEditAccount.header.title", { value: accountName })}
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        {
          to: "/depots-accounts",
          text: t("breadcrumbs.depotsAccounts"),
        },
        {
          to: `${url}/${accountId}`,
          text: truncate(accountName),
        },
        {
          text: t("createAndEditAccount.editBreadcumb"),
        },
      ]}
    >
      <Container data-testid="account-edit-form">
        <CreateAndUpdateResultMessage
          message={error?.response?.data?.message}
          type={requestStatus}
        />

        <CreateAndEditAccountForm
          accountValues={accountValues}
          hideFields={{
            bank: Boolean(account?.bankConnectionType),
            name: false,
            type: true,
          }}
          defaultBank={bank}
          onUpdateAccountValues={handleUpdateAccountValues}
        />

        <AccountEditActions accountId={accountId} accountType={account?.type} />

        <SaveButton
          data-testid="save-button"
          variant="primary"
          disabled={isAccountsLoading}
          onClick={handleUpdateAccount}
        >
          {t("createAndEditAccount.actions.save")}
        </SaveButton>
      </Container>
    </PageLayout>
  );
};
